import React, { useMemo } from 'react';
import { ThemeProvider, StyledEngineProvider, CssBaseline, Box, IconButton, Tooltip } from '@mui/material';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import { useNewUI } from 'hooks/useNewUI';
import { buildTheme } from './theme';

// `withBaseline` controls whether <CssBaseline /> is rendered. MUI's
// CssBaseline writes global `html`/`body`/`*` resets, so when V2Sidebar
// mounts on a classic page (e.g. /profile) it would silently strip
// margins/box-sizing across the whole page — that's what was crushing
// Swiper slide gaps on the profile category strip. V2 pages keep the
// baseline (the default); V2Sidebar passes `withBaseline={false}`.
export const V2ThemeProvider = ({ children, withBaseline = true }) => {
  const { themeMode } = useNewUI();
  const theme = useMemo(() => buildTheme(themeMode), [themeMode]);
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        {withBaseline && <CssBaseline />}
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export const V2FloatingControls = () => {
  const { themeMode, toggleTheme } = useNewUI();
  return (
    <Box
      sx={{
        position: 'fixed',
        right: 16,
        bottom: 16,
        zIndex: 1200,
        display: 'flex',
        gap: 1,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 999,
        p: 0.5,
        boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
      }}
    >
      <Tooltip title={`Switch to ${themeMode === 'dark' ? 'light' : 'dark'} mode`}>
        <IconButton size="small" onClick={toggleTheme}>
          {themeMode === 'dark' ? (
            <LightModeRoundedIcon fontSize="small" />
          ) : (
            <DarkModeRoundedIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export const V2Page = ({ children, maxWidth = 1280, sx }) => (
  <V2ThemeProvider>
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        color: 'text.primary',
        pt: { xs: 10, md: 12 },
        pb: 8,
        ...sx,
      }}
    >
      <Box sx={{ maxWidth, mx: 'auto', px: { xs: 2, md: 4 } }}>{children}</Box>
      <V2FloatingControls />
    </Box>
  </V2ThemeProvider>
);
